/*
 * globe.js — exports-globe section module
 *
 * Ported from the prototype's draw(): fibonacci-sphere point cloud on a
 * radial pressed-leaf ground, bone-coloured dots, leaf-green quadratic arcs
 * from origin to each destination, mono labels on the near face.
 *
 * Geometry, colours, sizes and the 0.0016 rad/frame rotation are unchanged.
 * Destinations and origin now come from the section's data attributes rather
 * than a hardcoded array.
 *
 * Four things the brief adds on top of the prototype:
 *
 *   1. The rAF loop is gated by IntersectionObserver. Off screen, the loop is
 *      cancelled outright — not throttled — so it costs nothing while a buyer
 *      reads the footer.
 *   2. Below 768px the globe is never initialised and the canvas is removed
 *      from the accessibility tree and the layout. The destination table is
 *      rendered server-side and stands on its own.
 *   3. prefers-reduced-motion freezes rotation. The globe is still drawn, once,
 *      so the section is not an empty box; it simply does not move.
 *   4. Torn down and rebuilt on shopify:section:unload / :load. The canvas
 *      context does not survive a theme-editor re-render.
 */

const instances = new Map();
/*
 * Originally 768: the launch brief skipped the globe on phones to spare CPU.
 * Reversed at the client's request (2026-08) — the globe now runs at every
 * width. Set back to 768 to restore the phone opt-out. The other power guards
 * (rAF cancelled off-screen, frozen under prefers-reduced-motion, DPR capped
 * at 2) still apply everywhere.
 */
const MOBILE_BREAKPOINT = 0;
const POINT_COUNT = 1500;

class ExportsGlobe {
  constructor(root) {
    this.root = root;
    this.canvas = root.querySelector('canvas');

    this.frame = null;
    this.visible = false;
    this.running = false;
    this.destroyed = false;
    this.angle = 0;

    this.dpr = Math.min(window.devicePixelRatio || 1, 2);

    this.origin = {
      la: parseFloat(root.dataset.originLat),
      lo: parseFloat(root.dataset.originLng),
    };

    try {
      this.dests = JSON.parse(root.dataset.destinations || '[]');
    } catch (e) {
      this.dests = [];
    }

    this.onResize = window.Bioverde.rafThrottle(() => this.handleResize());
    this.draw = this.draw.bind(this);

    this.init();
  }

  get isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  init() {
    if (!this.canvas) return;

    window.addEventListener('resize', this.onResize, { passive: true });

    if (this.isMobile) {
      this.disableCanvas();
      return;
    }

    this.enableCanvas();
  }

  /* ---------------- setup / teardown of the canvas itself ---------------- */

  disableCanvas() {
    this.stop();
    this.canvas.hidden = true;
    this.canvas.setAttribute('aria-hidden', 'true');
    this.ctx = null;
    this.points = null;
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  enableCanvas() {
    if (this.ctx) return;

    this.canvas.hidden = false;
    this.canvas.setAttribute('aria-hidden', 'true'); // decorative; the table carries the data
    this.ctx = this.canvas.getContext('2d');
    if (!this.ctx) return;

    this.buildPoints();
    this.size();
    this.observe();

    // Canvas text does not wait for webfonts. Redraw once Plex Mono is ready
    // so the destination labels are not stuck in the fallback face.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!this.destroyed && this.ctx && !this.running) this.draw(true);
      });
    }
  }

  buildPoints() {
    const pts = [];
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < POINT_COUNT; i++) {
      const y = 1 - (i / (POINT_COUNT - 1)) * 2;
      const rad = Math.sqrt(1 - y * y);
      const th = i * golden;
      pts.push([Math.cos(th) * rad, y, Math.sin(th) * rad]);
    }
    this.points = pts;
  }

  size() {
    const w = this.canvas.clientWidth;
    let h = this.canvas.clientHeight;
    if (w && !h) {
      // aspect-ratio fallback: on engines without it (older iOS Safari) the
      // canvas collapses to 0px tall and the globe silently vanished on
      // phones. Square it explicitly and re-measure.
      this.canvas.style.height = Math.min(w, 440) + 'px';
      h = this.canvas.clientHeight;
    }
    if (!w || !h) return;
    this.canvas.width = w * this.dpr;
    this.canvas.height = h * this.dpr;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  handleResize() {
    if (this.destroyed) return;

    if (this.isMobile) {
      if (this.ctx) this.disableCanvas();
      return;
    }

    if (!this.ctx) {
      this.enableCanvas();
      return;
    }

    this.size();
    if (!this.running) this.draw(true);
  }

  /* ---------------- visibility gating ---------------- */

  observe() {
    if (!('IntersectionObserver' in window)) {
      this.visible = true;
      this.start();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        this.visible = entry.isIntersecting;
        if (this.visible) {
          this.start();
        } else {
          this.stop();
        }
      },
      { threshold: 0 }
    );
    this.observer.observe(this.root);
  }

  start() {
    if (this.running || this.destroyed || !this.ctx) return;

    // Reduced motion: draw the globe once, then leave it alone. No loop.
    if (window.Bioverde.prefersReducedMotion()) {
      this.draw(true);
      return;
    }

    this.running = true;
    this.frame = requestAnimationFrame(this.draw);
  }

  stop() {
    this.running = false;
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  /* ---------------- projection helpers (prototype, unchanged) ---------------- */

  static toXYZ(la, lo) {
    const p = ((90 - la) * Math.PI) / 180;
    const t = ((lo + 180) * Math.PI) / 180;
    return [-Math.sin(p) * Math.cos(t), Math.cos(p), Math.sin(p) * Math.sin(t)];
  }

  static rotY(v, a) {
    const c = Math.cos(a);
    const s = Math.sin(a);
    return [v[0] * c + v[2] * s, v[1], -v[0] * s + v[2] * c];
  }

  /* ---------------- render ---------------- */

  draw(once) {
    if (this.destroyed || !this.ctx) return;

    const ctx = this.ctx;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    const cx = w / 2;
    const cy = h / 2;
    const R = Math.min(w, h) * 0.42;

    ctx.clearRect(0, 0, w, h);

    const g = ctx.createRadialGradient(cx, cy, R * 0.7, cx, cy, R * 1.2);
    g.addColorStop(0, 'rgba(58,107,75,.30)');
    g.addColorStop(1, 'rgba(31,51,38,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, R * 1.2, 0, 6.284);
    ctx.fill();

    for (let i = 0; i < this.points.length; i++) {
      const v = ExportsGlobe.rotY(this.points[i], this.angle);
      if (v[2] < -0.05) continue;
      const d = (v[2] + 1) / 2;
      ctx.fillStyle = 'rgba(235,231,217,' + (0.05 + d * 0.28).toFixed(3) + ')';
      ctx.beginPath();
      ctx.arc(cx + v[0] * R, cy - v[1] * R, 0.5 + d * 0.95, 0, 6.284);
      ctx.fill();
    }

    const o = ExportsGlobe.rotY(ExportsGlobe.toXYZ(this.origin.la, this.origin.lo), this.angle);
    const ox = cx + o[0] * R;
    const oy = cy - o[1] * R;

    for (let j = 0; j < this.dests.length; j++) {
      const dest = this.dests[j];
      const v = ExportsGlobe.rotY(ExportsGlobe.toXYZ(dest.la, dest.lo), this.angle);
      const x = cx + v[0] * R;
      const y = cy - v[1] * R;

      if (v[2] > 0 && o[2] > 0) {
        const mx = (ox + x) / 2;
        const my = (oy + y) / 2;
        const dx = x - ox;
        const dy = y - oy;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const nx = -dy / len;
        const ny = dx / len;
        const lift = len * 0.28;
        ctx.strokeStyle = 'rgba(150,196,163,' + (0.2 + v[2] * 0.4).toFixed(2) + ')';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(ox, oy);
        ctx.quadraticCurveTo(mx + nx * lift, my + ny * lift, x, y);
        ctx.stroke();
      }

      if (v[2] > 0.02) {
        ctx.fillStyle = 'rgba(150,196,163,' + (0.55 + v[2] * 0.45).toFixed(2) + ')';
        ctx.beginPath();
        ctx.arc(x, y, 2.1, 0, 6.284);
        ctx.fill();

        if (v[2] > 0.45) {
          ctx.fillStyle = 'rgba(235,231,217,' + (v[2] * 0.7).toFixed(2) + ')';
          ctx.font = '400 9px "IBM Plex Mono", monospace';
          ctx.fillText(dest.n, x + 9, y + 3);
        }
      }
    }

    if (o[2] > 0.02) {
      ctx.fillStyle = '#EBE7D9';
      ctx.beginPath();
      ctx.arc(ox, oy, 3, 0, 6.284);
      ctx.fill();
      ctx.strokeStyle = 'rgba(235,231,217,.35)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(ox, oy, 8, 0, 6.284);
      ctx.stroke();
    }

    if (once === true || !this.running) return;

    this.angle += 0.0016;
    this.frame = requestAnimationFrame(this.draw);
  }

  destroy() {
    this.destroyed = true;
    this.stop();
    window.removeEventListener('resize', this.onResize);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.ctx = null;
    this.points = null;
  }
}

/* ------------------------------------------------------------------
 * Section lifecycle
 * ------------------------------------------------------------------ */

const SELECTOR = '[data-section-type="exports-globe"][data-globe-enabled="true"]';

function resolve(target) {
  if (!target) return null;
  return target.matches && target.matches(SELECTOR) ? target : target.querySelector(SELECTOR);
}

function mount(root) {
  if (!root || instances.has(root)) return;
  instances.set(root, new ExportsGlobe(root));
}

function unmount(root) {
  const instance = instances.get(root);
  if (!instance) return;
  instance.destroy();
  instances.delete(root);
}

function mountAll(scope) {
  (scope || document).querySelectorAll(SELECTOR).forEach(mount);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mountAll());
} else {
  mountAll();
}

document.addEventListener('shopify:section:load', (event) => {
  const root = resolve(event.target);
  if (root) mount(root);
});

document.addEventListener('shopify:section:unload', (event) => {
  const root = resolve(event.target);
  if (root) unmount(root);
});

export { ExportsGlobe, mount, unmount };
